import { schemePaired } from 'd3';

import { CustomLayer, CustomLayerProps } from '@nivo/line';
import { line, area } from 'd3-shape';
import { PqScaleType, transformPq } from 'utils/pqTransform';

export const MEAN_COLORS = schemePaired
  .filter((x, i) => i % 2 == 0)
  .map((c) => c + 'B9');
export const LINE_COLORS = schemePaired.filter((x, i) => i % 2 == 1);

/**
 * Return the mean PQ from a set of summary data
 * @param summaryData summary data to be used
 * @returns mean pq
 */
export const getMeanPq = (
  summaryData: reports.ProductSummaryPQWinRate,
): number =>
  summaryData?.allReportSummaries?.nodes
    .map((n) => parseFloat(n.pq))
    .reduce((a, b) => (a ? a : b), 0 as number);

/**
 * Return the polarization from a set of summary data
 * @param summaryData summary data to be used
 * @returns polarization
 */
export const getPolarization = (
  summaryData: reports.ProductSummaryPQWinRate,
): number =>
  summaryData?.allReportSummaries?.nodes
    .map((n) => parseFloat(n.polarization))
    .reduce((a, b) => (a ? a : b), 0 as number);

/**
 * Linearly interpolate 7-bin market preference data onto 9 integer bins.
 * The 7pt scale [1..7] is mapped linearly to [1..9], and the percentPop
 * values are interpolated at each integer point on the 9pt scale.
 */
export function interpolateTo9Bins(
  data: { pqRating: number; percentPop: number | string }[],
): { x: number; y: number }[] {
  const sorted = [...data].sort((a, b) => a.pqRating - b.pqRating);
  if (sorted.length === 0) return [];

  // Build lookup from 7pt rating to percentPop
  const valueMap = new Map(sorted.map((d) => [d.pqRating, typeof d.percentPop === 'string' ? parseFloat(d.percentPop) : d.percentPop]));

  const result: { x: number; y: number }[] = [];
  for (let bin9 = 1; bin9 <= 9; bin9++) {
    // Inverse transform: map 9pt bin back to 7pt position
    // transformPq maps 7pt → 9pt: pq9 = (pq7 - 1) * (8/6) + 1
    // Inverse: pq7 = (pq9 - 1) * (6/8) + 1
    const pq7 = (bin9 - 1) * (6 / 8) + 1;

    // Interpolate percentPop at this 7pt position
    const floor7 = Math.floor(pq7);
    const ceil7 = Math.ceil(pq7);

    let percentPop: number;
    if (floor7 === ceil7 || !valueMap.has(ceil7)) {
      percentPop = valueMap.get(floor7) ?? 0;
    } else {
      const floorVal = valueMap.get(floor7) ?? 0;
      const ceilVal = valueMap.get(ceil7) ?? 0;
      const frac = pq7 - floor7;
      percentPop = floorVal + (ceilVal - floorVal) * frac;
    }

    result.push({ x: bin9, y: percentPop });
  }
  return result;
}

/**
 * Render a pq mean layer with polarization box in nivo
 * @param summaryData summary data containing mean PQ and polarization
 * @param idx index of layer for coloring
 * @param hidden whether to hide the layer
 * @param scale the PQ scale type for positioning
 * @returns nivo custom layer
 */
export const pqMeanLayer: (
  summaryData: reports.ProductSummaryPQWinRate,
  idx: number,
  hidden?: boolean,
  scale?: PqScaleType,
) => CustomLayer = (summaryData, idx, hidden?, scale = '7pt') => (props: CustomLayerProps) => {
  if (hidden) {
    return <g></g>;
  }

  const { xScale, innerHeight } = props;

  const meanpq7 = getMeanPq(summaryData);
  const polarization = getPolarization(summaryData);

  const meanpq = scale === '9pt' ? transformPq(meanpq7, '9pt') : meanpq7;
  const pqSpread = scale === '9pt' ? polarization / 2 * (8 / 6) : polarization / 2;

  const slope =
    (xScale(Math.ceil(meanpq)) as number) -
    (xScale(Math.floor(meanpq)) as number);
  const xLocation =
    (xScale(Math.floor(meanpq)) as number) +
    (meanpq - Math.floor(meanpq)) * slope;

  const areaGenerator = area();
  const lineGenerator = line();
  return (
    <g>
      <path
        d={areaGenerator([
          [xLocation - slope * pqSpread, 0],
          [xLocation + slope * pqSpread, 0],
          [xLocation + slope * pqSpread, innerHeight],
          [xLocation - slope * pqSpread, innerHeight],
        ])}
        fill={MEAN_COLORS[idx]}
        stroke={MEAN_COLORS[idx]}
        style={{ pointerEvents: 'none', strokeWidth: '2' }}
      />
      <path
        d={lineGenerator([
          [xLocation, 0],
          [xLocation, innerHeight],
        ])}
        fill="none"
        stroke={LINE_COLORS[idx]}
        style={{
          pointerEvents: 'none',
          strokeWidth: '2',
          strokeDasharray: '4, 4',
        }}
      />
    </g>
  );
};
